import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = signed in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // If returning from Emergent OAuth, AuthCallback will set the user.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // If access token is missing/expired, try the refresh endpoint with a stored refresh token.
      const stored = localStorage.getItem("wylo_refresh_token");
      if (stored) {
        try {
          const { data: r } = await api.post("/auth/refresh", null, {
            headers: { Authorization: `Bearer ${stored}` },
          });
          if (r?.access_token) {
            localStorage.setItem("wylo_token", r.access_token);
            const { data: me } = await api.get("/auth/me");
            setUser(me);
            return;
          }
        } catch (_) { /* fall through */ }
      }
      // Not signed in (or refresh failed) - clear any stale tokens.
      localStorage.removeItem("wylo_token");
      localStorage.removeItem("wylo_refresh_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const persistTokens = (data) => {
    if (data?.access_token) localStorage.setItem("wylo_token", data.access_token);
    if (data?.refresh_token) localStorage.setItem("wylo_refresh_token", data.refresh_token);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persistTokens(data);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.pending_review) {
      return { pendingReview: true, message: data.message };
    }
    persistTokens(data);
    setUser(data);
    return { user: data };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem("wylo_token");
    localStorage.removeItem("wylo_refresh_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser, persistTokens }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiError };
